import { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { copy, map } from 'ionicons/icons';
import type { GeocodePrecisionTier } from '@/types';

export interface PlusCodeDisplayProps {
  plusCode?: string;
  plusCodeShort?: string;
  geocodePrecision?: GeocodePrecisionTier;
  // Highest precision the viewer is allowed to see. 'Public' shows locality only.
  viewerTier?: GeocodePrecisionTier;
}

// Build the text copied to the clipboard — never the full coordinate.
export function buildClipboardText(short?: string, full?: string, viewerTier: GeocodePrecisionTier = 'Public'): string {
  return viewerTier === 'Public' ? (short ?? full ?? '') : (full ?? short ?? '');
}

/** Google Maps link — always uses the full plus code for unambiguous resolution. */
function mapsUrl(full?: string, short?: string): string | undefined {
  const code = full ?? short;
  if (!code) return undefined;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(code)}`;
}

export function PlusCodeDisplay({ plusCode, plusCodeShort, viewerTier = 'Public' }: PlusCodeDisplayProps) {
  const canExpand = viewerTier !== 'Public';
  // Default to the least-precision view — the viewer opts in to the full code.
  const [expanded, setExpanded] = useState(false);

  // Public viewers only ever see the locality short code.
  const showFull = canExpand && expanded;
  const displayed = showFull ? plusCode ?? plusCodeShort : plusCodeShort ?? plusCode;

  if (!displayed) return null;

  const copyText = buildClipboardText(plusCodeShort, plusCode, viewerTier);
  const href = mapsUrl(plusCode, plusCodeShort);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(copyText);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="bp-pluscode">
      <div className="bp-pluscode-row">
        {href ? (
          <a className="bp-pluscode-value" href={href} target="_blank" rel="noopener noreferrer">
            {displayed}
          </a>
        ) : (
          <code className="bp-pluscode-value">{displayed}</code>
        )}
        <button type="button" className="bp-pluscode-copy" onClick={handleCopy} aria-label="Copy Plus Code">
          <IonIcon icon={copy} />
        </button>
        {href && (
          <a className="bp-pluscode-copy" href={href} target="_blank" rel="noopener noreferrer" aria-label="Open in Google Maps">
            <IonIcon icon={map} />
          </a>
        )}
        {canExpand && (
          <button type="button" className="bp-pluscode-toggle" onClick={() => setExpanded((v) => !v)}>
            {showFull ? 'Locality' : 'Full'}
          </button>
        )}
      </div>
    </div>
  );
}
